import React, { useContext, useMemo, useState } from 'react';
import { Stack, Button, useTheme, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUploadAvatar } from 'hooks/useUploadAvatar';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { convertFileToBase64 } from 'helpers/fileHelper';
import { useDeleteAvatar } from 'hooks/useDeleteAvatar';
import { UserProfileViewContext } from 'context/userProfile/UserProfileViewContext';
import { EmptyUserAvatar } from 'components/EmptyUserAvatar/EmptyUserAvatar';
import { useSnackbar } from 'notistack';
import { AvatarButton, AvatarLoadingButton, CancelButton, DeleteButton } from './AvatarComponent.styles';

const StyledAvatarImage = styled('img')({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  objectFit: 'cover'
});

const StyledHiddenInput = styled('input')({
  display: 'none'
});

export const AvatarComponent = () => {
    const [file, setFile] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileExtension, setFileExtension] = useState<string | null>(null);
    const { t } = useTranslation();
    const theme = useTheme();
    const { user, isProfileOfLoggedUser, refreshProfile } = useContext(UserProfileViewContext);
    const { deleteAvatar, deleteAvatarPending } = useDeleteAvatar();
    const { uploadAvatar, uploadAvatarPending } = useUploadAvatar();
    const { enqueueSnackbar } = useSnackbar();

    const profilePhotoUrl = useMemo(() => { return user?.profilePhotoUrl }, [user]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        
        if (file) {
          if (!file.type.startsWith('image/')) {
            enqueueSnackbar(t('profile.wrongFileTypeMessage'), { variant: 'error' });
            return;
          }
    
          setSelectedFile(file);
          setFile(URL.createObjectURL(file));
          const extension = file.name.split('.').pop();
          setFileExtension(extension || null);
        }
      };
      
    const handleChooseFileClick = () => {
        document.getElementById('file-upload')?.click();
    };

    const handleFileResetClick = () => {
        setFile(null);
        setFileExtension(null);
    };

    const handleSaveAvatarClick = async () => {
        if (file && fileExtension) {
            const base64String = await convertFileToBase64(selectedFile);
            const base64WithoutPrefix = (base64String as string).split(',')[1];

            const resp = await uploadAvatar({ fileBytes: base64WithoutPrefix, fileExtension, userId: isProfileOfLoggedUser ? undefined : user?.id });
            if (resp?.success) {
                setFile(null);
                refreshProfile();
            }
        }
    }

    const handleDeleteClick = async () => {
        const resp = await deleteAvatar(isProfileOfLoggedUser ? undefined : user?.id);
        if (resp?.success) {
            refreshProfile();
        }
    }

    const componentStage: 'no-image' | 'image-downloaded' | 'image-selected' = useMemo(() => {
        if (file) {
            return 'image-selected';
        } else if (profilePhotoUrl) {
            return 'image-downloaded';
        } else {
            return 'no-image';
        }
    }, [file, profilePhotoUrl]);

    function getButtonBasedOnStage() {
        switch (componentStage) {
          case 'no-image':
            return <AvatarButton variant='outlined' color='primary' onClick={handleChooseFileClick}>{t('profile.chooseFile')}</AvatarButton>;
          
          case 'image-downloaded':
            return <AvatarButton variant='outlined' color='primary' onClick={handleChooseFileClick}>{t('profile.changeAvatar')}</AvatarButton>;
          
          case 'image-selected':
            return <AvatarLoadingButton loading={uploadAvatarPending} variant='outlined' color='primary' onClick={handleSaveAvatarClick}>{t('profile.saveAvatar')}</AvatarLoadingButton>;
      
          default:
            return null;
        }
    }

    return (
        <Stack
            direction={'column'}
            width='30%'
            padding={theme.spacing(2)}
            display='flex'
            justifyContent={'center'}
            alignItems={'center'}
            height={'100%'}
            spacing={theme.spacing(2)}
        >
            {file
            ? <StyledAvatarImage src={file} alt="User Avatar" />
            : profilePhotoUrl 
                ? <StyledAvatarImage src={profilePhotoUrl} alt="User Avatar" />
                : <EmptyUserAvatar color='black' fontSize='40px' padding={theme.spacing(3)} />}

            {getButtonBasedOnStage()}
            {!!file && <CancelButton variant='text' onClick={handleFileResetClick}>{t('profile.cancelAvatar')}</CancelButton>}
            {!!profilePhotoUrl && !file && <DeleteButton loading={deleteAvatarPending} variant='text' onClick={handleDeleteClick}>{t('profile.deleteAvatar')}</DeleteButton>}
            <StyledHiddenInput type='file' accept="image/jpeg" id='file-upload' onChange={handleFileChange} />
        </Stack>
    );
};